import React, { useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Bell, BookOpenCheck, Clock, CreditCard, Loader2, Play, Star } from 'lucide-react';
import api from '../../lib/api';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—');

const Stat = ({ label, value, sub }) => (
  <div className="p-3 rounded-xl border bg-muted/30">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

const STAGE = {
  paid: { label: 'Paid', cls: 'bg-emerald-600 text-white' },
  checkout_started: { label: 'Clicked pay', cls: 'bg-amber-500 text-white' },
  viewed: { label: 'Only viewed', cls: 'bg-slate-200 text-slate-700' },
};

const PaywallSection = ({ paywall, lead }) => {
  const rows = paywall?.paywalls || [];
  const t = paywall?.totals || {};
  return (
    <div className="rounded-xl border overflow-hidden" data-testid="paywall-intent-section">
      <div className="px-3 py-2 bg-muted/40 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <CreditCard className="h-4 w-4 text-primary" />Paywalls &amp; payment intent
        </span>
        <span className="text-[11px] text-muted-foreground" data-testid="paywall-totals">
          {t.views || 0} view(s) · {t.checkouts || 0} pay click(s) · {t.paid || 0} paid · last {paywall?.window_days || 90} days
        </span>
        {lead && (
          <span className="ml-auto flex items-center gap-2">
            <Badge className={lead.is_hot ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-700'}
                   data-testid="paywall-lead-hot">
              {lead.is_hot ? 'HOT lead' : 'Not hot'}
            </Badge>
            <span className="text-[11px] text-muted-foreground" data-testid="paywall-lead-score">
              score {lead.score ?? '—'}/100 · hot at {lead.hot_threshold}
              {lead.hot_reason ? ` · ${lead.hot_reason.replace(/_/g, ' ')}` : ''}
            </span>
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="px-3 py-4 text-sm text-muted-foreground" data-testid="paywall-empty">
          She has not opened any paywall yet.
        </p>
      ) : (
        <div className="divide-y">
          {rows.map((r) => {
            const st = STAGE[r.furthest] || STAGE.viewed;
            return (
              <div key={r.paywall} className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                   data-testid={`paywall-row-${r.paywall}`}>
                <span className="font-medium min-w-[150px]">{r.label}</span>
                <Badge className={`${st.cls} text-[10px]`} data-testid={`paywall-stage-${r.paywall}`}>{st.label}</Badge>
                <span className="text-xs text-muted-foreground">
                  {r.views} view(s){r.last_viewed_at ? ` · last ${fmtDateTime(r.last_viewed_at)}` : ''}
                </span>
                {r.checkouts > 0 && (
                  <span className="text-xs text-amber-700" data-testid={`paywall-checkout-${r.paywall}`}>
                    {r.checkouts} pay click(s) · last {fmtDateTime(r.last_checkout_at)}
                  </span>
                )}
                {r.paid > 0 && (
                  <span className="text-xs text-emerald-700" data-testid={`paywall-paid-${r.paywall}`}>
                    paid {fmtDateTime(r.last_paid_at)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      {lead?.reasons?.length > 0 && (
        <p className="px-3 py-2 text-[11px] text-muted-foreground border-t" data-testid="paywall-score-reasons">
          Score: {lead.reasons.join(' · ')}
        </p>
      )}
    </div>
  );
};

export const SoulAIUsageTab = ({ userId }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setData(null); setErr(null);
    api.get(`/api/admin/soulai/usage/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Could not load SoulAI usage'));
  }, [userId]);

  if (err) return <p className="text-center text-sm text-muted-foreground py-8" data-testid="soulai-usage-error">{String(err)}</p>;
  if (!data) {
    return (
      <div className="flex justify-center py-8" data-testid="soulai-usage-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const s = data.summary || {};
  const history = data.watch_history || [];
  const upcoming = data.upcoming || [];

  return (
    <div className="space-y-5" data-testid="soulai-usage-tab">
      {!data.onboarded && (
        <p className="text-xs text-muted-foreground" data-testid="soulai-not-onboarded">
          She hasn't completed SoulAI Coach onboarding — anything below is from earlier plans.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="soulai-usage-summary">
        <Stat label="Classes watched" value={`${s.classes_completed ?? 0}/${s.classes_planned ?? 0}`} sub={`${s.classes_started ?? 0} started`} />
        <Stat label="Minutes watched" value={s.minutes_watched ?? 0} />
        <Stat label="Avg rating" value={s.avg_rating ?? '—'} sub="out of 5" />
        <Stat label="Last watched" value={s.last_watched_at ? fmtDate(s.last_watched_at) : '—'} sub={`${s.weeks_planned ?? 0} week plan(s)`} />
      </div>

      <PaywallSection paywall={data.paywall} lead={data.lead_score} />

      <div className="p-3 rounded-xl border flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="soulai-reminder">
        <span className="flex items-center gap-1.5 font-medium"><Bell className="h-4 w-4 text-amber-600" />Reminder</span>
        <span className="text-muted-foreground">
          {data.reminder?.reminder_time ? `Daily at ${data.reminder.reminder_time}` : 'No reminder time set'}
          {data.reminder?.timezone ? ` (${data.reminder.timezone})` : ''}
        </span>
        <span className="text-muted-foreground">
          Practice days: {(data.reminder?.practice_days || []).join(', ') || '—'}
        </span>
      </div>

      <div>
        <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-sky-600" />Scheduled next ({upcoming.length})
        </p>
        {upcoming.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="soulai-upcoming-empty">No upcoming SoulAI classes scheduled.</p>
        ) : (
          <div className="space-y-2" data-testid="soulai-upcoming-list">
            {upcoming.map((r, i) => (
              <div key={`${r.video_id}-${i}`} className="p-3 rounded-xl border flex items-center justify-between gap-3" data-testid={`soulai-upcoming-row-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.title || 'Class'}</p>
                  <p className="text-xs text-muted-foreground">
                    {fmtDate(r.date)}{r.day_label ? ` · ${r.day_label}` : ''} · {r.duration_minutes} min
                    {r.focus_area ? ` · ${r.focus_area}` : ''}
                  </p>
                </div>
                <Badge variant="outline" className="shrink-0 text-[11px]">
                  {data.reminder?.reminder_time ? `Reminder ${data.reminder.reminder_time}` : 'Scheduled'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
          <BookOpenCheck className="h-4 w-4 text-emerald-600" />Watch history ({history.length})
        </p>
        {history.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="soulai-history-empty">No SoulAI class has been opened yet.</p>
        ) : (
          <div className="space-y-2" data-testid="soulai-history-list">
            {history.map((r, i) => (
              <div key={`${r.video_id}-${i}`} className="p-3 rounded-xl border hover:bg-muted/20 transition-colors" data-testid={`soulai-history-row-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <Play className="h-4 w-4 text-emerald-700" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.title || 'Class'}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {r.focus_area || '—'}{r.intensity ? ` · ${r.intensity}` : ''}
                        {r.source === 'extra' ? ' · extra class' : ''}
                        {r.pregnancy_week ? ` · week ${r.pregnancy_week}` : ''}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {fmtDate(r.date)} · watched <strong>{r.watched_minutes}</strong> of {r.duration_minutes} min
                        {r.last_watched_at ? ` · last seen ${fmtDateTime(r.last_watched_at)}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge
                      variant="outline"
                      className={r.status === 'completed'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : r.watch_pct > 0 ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-muted text-muted-foreground'}
                      data-testid={`soulai-history-pct-${i}`}
                    >
                      {r.watch_pct}% {r.status === 'completed' ? 'complete' : r.status}
                    </Badge>
                    {r.rating ? (
                      <span className="text-xs flex items-center gap-0.5 text-amber-700" data-testid={`soulai-history-rating-${i}`}>
                        <Star className="h-3 w-3 fill-amber-400 text-amber-500" />{r.rating}/5
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">no feedback</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
